/**
 * MARK:- Cart Shipping Restrictions Feature
 * Client-side logic for checking shipping restrictions on cart page
 *
 * This script handles:
 * 1. Checking all cart items against customer's growing zone
 * 2. Displaying restriction badges on affected line items
 * 3. Controlling checkout button state based on restrictions
 * 4. Re-evaluating when zone changes or cart updates
 * 5. (#2920) Line badges with Remove in the cart drawer too: loaded site-wide from theme.liquid; without the
 *    cart page's summary container only the line badges run. State order: a Plant Sentry 422's state
 *    (NHVolumeSavings.sentryShipState, the cart's delivery address), then the zone widget, then the cart attribute.
 *
 * CHECKOUT STATES:
 * 1. Zone unknown -> Disable checkout, show "Enter Growing Zone" prompt
 * 2. Zone known + restrictions -> Disable checkout, show restricted items
 * 3. Zone known + no restrictions -> Enable checkout
 */

(function() {
  'use strict';

  // MARK:- Prevent multiple initializations
  if (window.cartShippingRestrictionsInitialized) {
    return;
  }
  window.cartShippingRestrictionsInitialized = true;

  /**
   * MARK:- Main initialization
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartRestrictions);
  } else {
    initCartRestrictions();
  }

  /**
   * MARK:- Initialize cart shipping restrictions
   */
  function initCartRestrictions() {
    refreshCartRestrictions();

    // MARK:- Section replacement creates a new container; always resolve the live one.
    document.addEventListener('zone:updated', (event) => {
      refreshCartRestrictions(event.detail);
    });
    // MARK:- #2899: nhzone:changed carries the persisted shopper zone (custom-zone-verdict.js)
    for (const eventName of ['on:line-item:change', 'on:cart-items:refresh', 'nhzone:changed', 'nh:sentry-state']) {
      document.addEventListener(eventName, () => refreshCartRestrictions());
    }
    // MARK:- #2920 The drawer re-renders by innerHTML with no event; re-judge when cart rows land
    new MutationObserver((records) => {
      const landed = records.some((record) => Array.prototype.some.call(record.addedNodes, (node) =>
        node.nodeType === 1 && (node.matches('cart-items, .cart-item') || node.querySelector('cart-items, .cart-item'))));
      if (landed) refreshCartRestrictions();
    }).observe(document.body, { childList: true, subtree: true });
  }

  function sentryState() {
    return window.NHVolumeSavings ? window.NHVolumeSavings.sentryShipState() : null;
  }

  /**
   * Reconcile replacement markup synchronously, before the browser paints a ZIP prompt.
   * @param {object} [location] - Latest zone update, when provided
   */
  function refreshCartRestrictions(location) {
    const container = document.querySelector('.cart-restriction-container');
    if (!container || container.dataset.enabled !== 'true') {
      refreshDrawerBadges(location);
      return;
    }

    const config = {
      enabled: true,
      serverStatus: container.dataset.serverStatus,
      cartState: container.dataset.cartState,
      itemsRestrictions: parseItemsRestrictions(container.dataset.itemsRestrictions),
      cartGrowingZone: container.dataset.cartGrowingZone || ''
    };
    container._cartRestrictionConfig = config;

    let savedState;
    let savedZip;
    try {
      savedState = localStorage.getItem('growing_state');
      savedZip = localStorage.getItem('growing_zipcode');
    } catch {
      // Storage may be blocked; the server location remains usable.
    }
    const state = location ? location.stateCode : sentryState() || savedState || config.cartState;
    // MARK:- innerHTML does not execute the snippet's inline pending-location script.
    if (!state && savedZip && !container.hasAttribute('data-location-pending')) {
      container.setAttribute('data-location-pending', '');
      clearTimeout(container._locationPendingTimer);
      container._locationPendingTimer = setTimeout(() => {
        container.removeAttribute('data-location-pending');
      }, 3000);
    }
    evaluateRestrictions(container, state);
  }

  /**
   * MARK:- #2920 Line badges only (cart drawer off the cart page). Unknown state leaves the Liquid badges alone.
   * @param {object} [location] - Latest zone update, when provided
   */
  function refreshDrawerBadges(location) {
    let savedState = null;
    try {
      savedState = localStorage.getItem('growing_state');
    } catch {
      // Storage may be blocked; the cart attribute remains usable.
    }
    const cartItems = document.querySelector('cart-items');
    const state = location ? location.stateCode : sentryState() || savedState || (cartItems && cartItems.dataset.cartState);
    if (!state) return;
    updateLineItemBadges(state);
    if (window.NHCartZoneSync) window.NHCartZoneSync.renderBadges();
  }

  /**
   * MARK:- Parse items restrictions from data attribute
   * Format: productId:variantId:restrictions|productId:variantId:restrictions|...
   * @param {string} data - The raw data string
   * @returns {array} - Array of { productId, variantId, restrictions[] }
   */
  function parseItemsRestrictions(data) {
    if (!data) return [];

    return data.split('|').map(item => {
      const parts = item.split(':');
      return {
        productId: parts[0],
        variantId: parts[1],
        restrictions: parts[2] ? parts[2].split(',').map(s => s.trim().toUpperCase()) : []
      };
    }).filter(item => item.restrictions.length > 0);
  }

  /**
   * MARK:- Evaluate restrictions and update UI
   * @param {HTMLElement} container - The restriction container
   * @param {string|null} stateCode - Customer's state code
   */
  function evaluateRestrictions(container, stateCode) {
    const config = container._cartRestrictionConfig;

    if (!config) return;

    // MARK:- Determine status
    let status = 'unknown';
    let restrictedItems = [];

    if (stateCode) {
      const normalizedState = stateCode.toUpperCase();
      status = 'clear';

      // Check each item for restrictions
      config.itemsRestrictions.forEach(item => {
        if (item.restrictions.includes(normalizedState)) {
          status = 'restricted';
          restrictedItems.push(item);
        }
      });
    }

    // MARK:- Update notice display
    updateNotices(container, status, restrictedItems.length);

    // MARK:- Update line item badges
    updateLineItemBadges(stateCode);

    // MARK:- Update checkout button
    updateCheckoutButton(status, stateCode);

    // MARK:- Evaluate zone protection (informational only)
    evaluateZoneProtection(container);
  }

  /**
   * MARK:- Evaluate zone protection for cart items (#2899)
   * Informational only - never blocks checkout. Judges each plant row with NHZone against the
   * shopper zone, counts out_of_zone lines for the summary, and re-renders the line badges.
   * @param {HTMLElement} container - The restriction container
   */
  function evaluateZoneProtection(container) {
    if (!window.NHZone) return;
    const zone = window.NHZone.currentZone();
    let outOfZone = 0;
    document.querySelectorAll('.cart-item[data-line-key]').forEach((row) => {
      const result = window.NHZone.judge({
        zoneRange: row.dataset.zoneRange || '',
        zoneList: parseJson(row.dataset.zoneList),
        productType: row.dataset.productType || '',
        tags: parseJson(row.dataset.productTags)
      }, zone);
      if (result.verdict === 'out_of_zone') outOfZone += 1;
    });

    // MARK:- Update zone warning notice
    updateZoneWarningNotice(container, outOfZone, window.NHZone.zoneLabel(zone));

    // MARK:- Update line item zone badges (shared renderer, also used in the drawer)
    if (window.NHCartZoneSync) window.NHCartZoneSync.renderBadges(zone);
  }

  function parseJson(value) {
    try {
      return JSON.parse(value || '[]');
    } catch {
      return [];
    }
  }

  /**
   * MARK:- Update notice visibility
   * @param {HTMLElement} container - The restriction container
   * @param {string} status - Current status (unknown, restricted, clear)
   * @param {number} restrictedCount - Number of restricted items
   */
  function updateNotices(container, status, restrictedCount) {
    const notices = container.querySelectorAll('.cart-restriction-notice');

    notices.forEach(notice => {
      const noticeType = notice.dataset.noticeType;
      if (noticeType === status) {
        notice.classList.remove('hide');
      } else {
        notice.classList.add('hide');
      }
    });
    // MARK:- Reveal resolved state immediately; an unresolved saved ZIP retains its bounded fallback.
    if (status !== 'unknown') {
      clearTimeout(container._locationPendingTimer);
      container.removeAttribute('data-location-pending');
    }

    // MARK:- Update restricted count text
    if (status === 'restricted') {
      const countEl = container.querySelector('.js-restricted-count');
      if (countEl) {
        countEl.textContent = restrictedCount;
      }
      const countContainer = container.querySelector('.cart-restriction-notice__count');
      if (countContainer) {
        // Update plural/singular
        const itemText = restrictedCount === 1 ? 'item' : 'items';
        countContainer.innerHTML = `(<span class="js-restricted-count">${restrictedCount}</span> ${itemText} cannot ship to your area)`;
      }
    }
  }

  /**
   * MARK:- Update line item badges
   * Updates the server-rendered badge containers with current restriction status.
   * Uses existing .cart-item__restriction-badge-container elements instead of creating new ones.
   * #2920: reads each row's own data-restricted-states, so the drawer and cart page rows both judge.
   * @param {string|null} stateCode - Customer's state code
   */
  function updateLineItemBadges(stateCode) {
    const cartItems = document.querySelectorAll('.cart-item');
    const fromSentry = Boolean(stateCode) && String(stateCode).toUpperCase() === sentryState();

    cartItems.forEach(cartItem => {
      // MARK:- Find the server-rendered badge container
      const badgeContainer = cartItem.querySelector('.cart-item__restriction-badge-container');
      if (!badgeContainer) return;

      // MARK:- Clear the has-restriction class first
      cartItem.classList.remove('cart-item--has-restriction');

      // MARK:- Check if this item is restricted
      let isRestricted = false;
      if (stateCode) {
        const restrictions = (cartItem.dataset.restrictedStates || '').split(',').map(s => s.trim().toUpperCase());
        isRestricted = restrictions.includes(stateCode.toUpperCase());
      }

      // MARK:- Update badge container content; an unchanged badge is left alone (the observer re-runs often)
      const badgeKey = isRestricted ? `${stateCode.toUpperCase()}|${fromSentry}` : '';
      if (isRestricted) {
        if (badgeContainer.dataset.badgeKey !== badgeKey || !badgeContainer.querySelector('.cart-item__restriction-badge')) {
          const remove = cartItem.querySelector('.cart-item__remove');
          badgeContainer.innerHTML = createRestrictionBadgeHTML(stateCode, fromSentry, remove ? remove.dataset.index : '', badgeContainer.dataset.swapUrl);
        }
        cartItem.classList.add('cart-item--has-restriction');
      } else if (badgeContainer.querySelector('.cart-item__restriction-badge')) {
        // MARK:- Clear the badge if not restricted (the zone badge renderer refills the container)
        badgeContainer.innerHTML = '';
      }
      badgeContainer.dataset.badgeKey = badgeKey;
    });
  }

  /**
   * MARK:- Create restriction badge HTML string
   * Returns HTML string to be inserted into the server-rendered container.
   * Mirrors the Liquid badge in snippets/cart-items.liquid (#2920: Remove chip, similar-plants link).
   * @param {string} stateCode - The state code
   * @param {boolean} fromSentry - The state came from a Plant Sentry block (the delivery address)
   * @param {string} lineIndex - The row's 1-based line index for cart-items.js removal
   * @param {string} [swapUrl] - Product page alternatives anchor, when the plant has similar plants
   * @returns {string} - The badge HTML
   */
  function createRestrictionBadgeHTML(stateCode, fromSentry, lineIndex, swapUrl) {
    const state = stateCode.toUpperCase().replace(/[^A-Z]/g, '');
    const label = fromSentry ? `Cannot ship to your ${state} delivery address` : `Cannot ship to ${state}`;
    const remove = lineIndex ? `<button type="button" class="cart-item__restriction-action js-remove-item" data-index="${Number(lineIndex)}">Remove</button>` : '';
    const swap = swapUrl && swapUrl.startsWith('/') ? `<a class="cart-item__restriction-action" href="${swapUrl.replace(/"/g, '&quot;')}">See similar plants</a>` : '';
    return `
      <div class="cart-item__restriction-badge">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
        <span>${label}</span>
      </div>
      ${remove || swap ? `<div class="cart-item__restriction-actions">${remove}${swap}</div>` : ''}
    `;
  }

  /**
   * MARK:- Update zone warning content visibility
   * Swaps between "Plant Sentry protected" and "{n} plant(s) outside zone {zone}" in the clear notice
   * @param {HTMLElement} container - The restriction container
   * @param {number} zoneWarningCount - Number of items not zone protected
   */
  function updateZoneWarningNotice(container, zoneWarningCount, zoneLabel) {
    const clearNotice = container.querySelector('[data-notice-type="clear"]');
    if (!clearNotice) return;

    const protectedContent = clearNotice.querySelector('[data-zone-protected-content]');
    const notProtectedContent = clearNotice.querySelector('[data-zone-not-protected-content]');

    if (!protectedContent || !notProtectedContent) return;

    // MARK:- Swap content visibility based on zone warnings
    if (zoneWarningCount > 0) {
      protectedContent.classList.add('hide');
      notProtectedContent.classList.remove('hide');
      clearNotice.dataset.zoneWarningCount = zoneWarningCount;
      const text = notProtectedContent.querySelector('[data-zone-warning-text]');
      if (text) text.textContent = `${zoneWarningCount} plant(s) outside zone ${zoneLabel}`;
    } else {
      protectedContent.classList.remove('hide');
      notProtectedContent.classList.add('hide');
      clearNotice.dataset.zoneWarningCount = '0';
    }
  }

  /**
   * MARK:- Update checkout button state
   * @param {string} status - Current status (unknown, restricted, clear)
   * @param {string|null} stateCode - Customer's state code
   */
  function updateCheckoutButton(status, stateCode) {
    const checkoutButton = document.querySelector('button[name="checkout"]');

    if (!checkoutButton) return;

    // MARK:- Store original state if not stored
    if (!checkoutButton.dataset.originalText) {
      checkoutButton.dataset.originalText = checkoutButton.innerHTML;
      checkoutButton.dataset.originalDisabled = checkoutButton.disabled ? 'true' : 'false';
    }

    // MARK:- Get cart icon from original text
    const originalContent = checkoutButton.dataset.originalText;
    const iconMatch = originalContent.match(/<svg[^>]*>[\s\S]*?<\/svg>/i);
    const icon = iconMatch ? iconMatch[0] : '';

    if (status === 'unknown') {
      // MARK:- Zone unknown - block checkout
      checkoutButton.disabled = true;
      checkoutButton.innerHTML = icon + 'Enter Growing Zone to Checkout';
      checkoutButton.classList.add('btn--disabled-cart-restriction');
    } else if (status === 'restricted') {
      // MARK:- Restricted items - block checkout
      checkoutButton.disabled = true;
      checkoutButton.innerHTML = icon + 'Remove Restricted Items to Checkout';
      checkoutButton.classList.add('btn--disabled-cart-restriction');
    } else {
      // MARK:- All clear - enable checkout
      const wasOriginallyDisabled = checkoutButton.dataset.originalDisabled === 'true';
      checkoutButton.disabled = wasOriginallyDisabled;
      checkoutButton.innerHTML = checkoutButton.dataset.originalText;
      checkoutButton.classList.remove('btn--disabled-cart-restriction');
    }
  }

})();
